import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, RotateCcw, CheckCircle, XCircle, Search } from 'lucide-react';
import api from '../../services/api';
import Modal from '../../components/shared/Modal';

const EMPTY_ENDERECO = { cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: 'Maringá', estado: 'PR' };
const EMPTY_CONJUGE = { nome: '', cpf: '', email: '', dataNascimento: '', endereco: { ...EMPTY_ENDERECO } };
const EMPTY_FORM = {
  nome: '', email: '', cpf: '', telefone: '', data_nascimento: '',
  endereco: { ...EMPTY_ENDERECO },
  temConjuge: false,
  conjuge: { ...EMPTY_CONJUGE },
  crm_empreendimento_id: '',
  empreendimento_nome: '',
  unidade_numero: '',
  unidade_bloco: '',
};

function formatCpf(v) {
  return v.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4').slice(0, 14);
}
function formatCep(v) {
  return v.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2').slice(0, 9);
}

function Campo({ label, children, span2 }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, ...props }) {
  return (
    <input
      value={value}
      onChange={onChange}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
      {...props}
    />
  );
}

function EnderecoSection({ value, onChange, label }) {
  const set = (k) => (e) => onChange({ ...value, [k]: k === 'cep' ? formatCep(e.target.value) : e.target.value });
  return (
    <div>
      <p className="text-sm font-semibold text-gray-700 mb-3">{label}</p>
      <div className="grid grid-cols-4 gap-3">
        <Campo label="CEP"><Input value={value.cep || ''} onChange={set('cep')} placeholder="87010-000" /></Campo>
        <Campo label="Logradouro" span2><Input value={value.logradouro || ''} onChange={set('logradouro')} placeholder="Rua, Av..." /></Campo>
        <Campo label="Número"><Input value={value.numero || ''} onChange={set('numero')} placeholder="123" /></Campo>
        <Campo label="Complemento" span2><Input value={value.complemento || ''} onChange={set('complemento')} placeholder="Apto 42" /></Campo>
        <Campo label="Bairro"><Input value={value.bairro || ''} onChange={set('bairro')} placeholder="Centro" /></Campo>
        <Campo label="Cidade"><Input value={value.cidade || ''} onChange={set('cidade')} placeholder="Maringá" /></Campo>
        <Campo label="Estado"><Input value={value.estado || ''} onChange={set('estado')} placeholder="PR" /></Campo>
      </div>
    </div>
  );
}

export default function PortalClientesList() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [erro, setErro] = useState('');
  const [search, setSearch] = useState('');
  const [filtroEmp, setFiltroEmp] = useState('');
  const [tab, setTab] = useState('dados');

  const { data: clientes = [], isLoading } = useQuery({
    queryKey: ['portal-clientes'],
    queryFn: () => api.get('/portal-clientes').then(r => r.data.data),
  });

  const { data: empreendimentosData } = useQuery({
    queryKey: ['empreendimentos-select'],
    queryFn: () => api.get('/empreendimentos').then(r => r.data.data),
  });
  const empreendimentos = empreendimentosData || [];

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      editing
        ? api.put(`/portal-clientes/${editing.id}`, payload).then(r => r.data)
        : api.post('/portal-clientes', payload).then(r => r.data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['portal-clientes'] }); closeModal(); },
    onError: (err) => setErro(err.response?.data?.error || 'Erro ao salvar'),
  });

  const resetMutation = useMutation({
    mutationFn: (id) => api.post(`/portal-clientes/${id}/reset-senha`).then(r => r.data),
    onSuccess: () => alert('Nova senha temporária enviada por e-mail!'),
    onError: (err) => alert(err.response?.data?.error || 'Erro ao resetar senha'),
  });

  const listaFiltrada = useMemo(() => {
    return clientes.filter(c => {
      if (filtroEmp && c.crm_empreendimento_id !== filtroEmp) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        c.nome?.toLowerCase().includes(s) ||
        c.email?.toLowerCase().includes(s) ||
        c.cpf?.includes(search.replace(/\D/g, '')) ||
        c.empreendimento_nome?.toLowerCase().includes(s) ||
        c.unidade_numero?.toLowerCase().includes(s) ||
        c.unidade_bloco?.toLowerCase().includes(s)
      );
    });
  }, [clientes, search, filtroEmp]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setErro('');
    setTab('dados');
    setModal(true);
  }

  function openEdit(c) {
    setEditing(c);
    setForm({
      nome: c.nome || '',
      email: c.email || '',
      cpf: c.cpf || '',
      telefone: c.telefone || '',
      data_nascimento: c.data_nascimento || '',
      endereco: c.endereco || { ...EMPTY_ENDERECO },
      temConjuge: !!(c.conjuge?.nome),
      conjuge: c.conjuge || { ...EMPTY_CONJUGE },
      crm_empreendimento_id: c.crm_empreendimento_id || '',
      empreendimento_nome: c.empreendimento_nome || '',
      unidade_numero: c.unidade_numero || '',
      unidade_bloco: c.unidade_bloco || '',
      ativo: c.ativo,
    });
    setErro('');
    setTab('dados');
    setModal(true);
  }

  function closeModal() { setModal(false); setEditing(null); setErro(''); }

  function handleEmpSelect(e) {
    const id = e.target.value;
    const emp = empreendimentos.find(x => x.id === id);
    setForm(f => ({ ...f, crm_empreendimento_id: id, empreendimento_nome: emp?.nome || '' }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    setErro('');
    const payload = {
      nome: form.nome,
      email: form.email,
      cpf: form.cpf,
      telefone: form.telefone || null,
      data_nascimento: form.data_nascimento || null,
      endereco: form.endereco,
      conjuge: form.temConjuge && form.conjuge.nome
        ? { ...form.conjuge, cpf: form.conjuge.cpf?.replace(/\D/g, '') }
        : null,
      crm_empreendimento_id: form.crm_empreendimento_id || null,
      empreendimento_nome: form.empreendimento_nome || null,
      unidade_numero: form.unidade_numero || null,
      unidade_bloco: form.unidade_bloco || null,
    };
    if (editing) payload.ativo = form.ativo;
    saveMutation.mutate(payload);
  }

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setConj = (k) => (e) => setForm(f => ({ ...f, conjuge: { ...f.conjuge, [k]: e.target.value } }));

  const TABS = [
    { key: 'dados', label: 'Dados Pessoais' },
    { key: 'imovel', label: 'Imóvel' },
    { key: 'endereco', label: 'Endereço' },
    { key: 'conjuge', label: 'Cônjuge' },
  ];

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Portal do Cliente</h1>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary">
          <Plus size={16} /> Novo Cliente
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow p-4 flex gap-3">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome, e-mail, CPF, unidade..."
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
          />
        </div>
        <select
          value={filtroEmp}
          onChange={e => setFiltroEmp(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[220px]"
        >
          <option value="">Todos os empreendimentos</option>
          {empreendimentos.map(e => (
            <option key={e.id} value={e.id}>{e.nome}</option>
          ))}
        </select>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Carregando...</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-3 text-left">Nome</th>
                <th className="px-6 py-3 text-left">E-mail / CPF</th>
                <th className="px-6 py-3 text-left">Empreendimento</th>
                <th className="px-6 py-3 text-left">Unidade</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Senha</th>
                <th className="px-6 py-3 text-left"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {listaFiltrada.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-400">Nenhum cliente encontrado</td></tr>
              ) : listaFiltrada.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-800">{c.nome}</td>
                  <td className="px-6 py-4">
                    <p className="text-gray-700">{c.email}</p>
                    <p className="text-xs text-gray-400">{c.cpf}</p>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{c.empreendimento_nome || '—'}</td>
                  <td className="px-6 py-4 text-gray-600">
                    {c.unidade_numero
                      ? <span>{c.unidade_numero}{c.unidade_bloco ? ` · ${c.unidade_bloco}` : ''}</span>
                      : '—'}
                  </td>
                  <td className="px-6 py-4">
                    {c.ativo
                      ? <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle size={13} /> Ativo</span>
                      : <span className="flex items-center gap-1 text-xs text-red-500"><XCircle size={13} /> Inativo</span>}
                  </td>
                  <td className="px-6 py-4">
                    {c.senhaTemporaria
                      ? <span className="text-xs text-orange-500 font-medium">Temporária</span>
                      : <span className="text-xs text-green-600">Definida</span>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => { if (window.confirm(`Enviar nova senha por e-mail para ${c.nome}?`)) resetMutation.mutate(c.id); }}
                        title="Resetar senha"
                        className="text-gray-400 hover:text-orange-500 p-1 rounded"
                      >
                        <RotateCcw size={14} />
                      </button>
                      <button onClick={() => openEdit(c)} title="Editar" className="text-gray-400 hover:text-primary p-1 rounded">
                        <Pencil size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={modal} onClose={closeModal} title={editing ? 'Editar Cliente' : 'Novo Cliente'} size="xl">
        <form onSubmit={handleSubmit}>
          <div className="flex border-b border-gray-200 mb-6 -mt-2">
            {TABS.map(t => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === t.key ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Aba: Dados pessoais */}
          {tab === 'dados' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
                <input value={form.nome} onChange={setF('nome')} required className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
                <input type="email" value={form.email} onChange={setF('email')} required className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CPF *</label>
                <input value={form.cpf} onChange={e => setForm(f => ({ ...f, cpf: formatCpf(e.target.value) }))}
                  required placeholder="000.000.000-00" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input value={form.telefone} onChange={setF('telefone')} placeholder="(44) 99999-9999" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de nascimento</label>
                <input type="date" value={form.data_nascimento} onChange={setF('data_nascimento')} className={inputCls} />
              </div>
              {editing && (
                <div className="col-span-2 flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.ativo}
                      onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300" />
                    <span className="text-sm font-medium text-gray-700">Cliente ativo no portal</span>
                  </label>
                </div>
              )}
              {!editing && (
                <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  Uma senha temporária será gerada automaticamente e enviada para o e-mail do cliente.
                </div>
              )}
            </div>
          )}

          {/* Aba: Imóvel */}
          {tab === 'imovel' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empreendimento</label>
                <select value={form.crm_empreendimento_id} onChange={handleEmpSelect} className={inputCls}>
                  <option value="">Selecione o empreendimento...</option>
                  {empreendimentos.map(e => (
                    <option key={e.id} value={e.id}>{e.nome}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número da unidade</label>
                  <input value={form.unidade_numero} onChange={setF('unidade_numero')}
                    placeholder="Ex: 42, 304, Lote 12" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bloco / Torre</label>
                  <input value={form.unidade_bloco} onChange={setF('unidade_bloco')}
                    placeholder="Ex: Torre A, Bloco 2" className={inputCls} />
                </div>
              </div>
              {form.empreendimento_nome && (
                <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-600">
                  <span className="font-medium">Empreendimento selecionado:</span> {form.empreendimento_nome}
                  {form.unidade_numero && <span> · Unidade {form.unidade_numero}</span>}
                  {form.unidade_bloco && <span> · {form.unidade_bloco}</span>}
                </div>
              )}
            </div>
          )}

          {/* Aba: Endereço */}
          {tab === 'endereco' && (
            <EnderecoSection
              value={form.endereco}
              onChange={end => setForm(f => ({ ...f, endereco: end }))}
              label="Endereço do cliente"
            />
          )}

          {/* Aba: Cônjuge */}
          {tab === 'conjuge' && (
            <div className="space-y-5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.temConjuge}
                  onChange={e => setForm(f => ({ ...f, temConjuge: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300" />
                <span className="text-sm font-medium text-gray-700">Possui cônjuge / companheiro(a)</span>
              </label>
              {form.temConjuge && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo</label>
                      <input value={form.conjuge.nome} onChange={setConj('nome')} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
                      <input
                        value={form.conjuge.cpf || ''}
                        onChange={e => setForm(f => ({ ...f, conjuge: { ...f.conjuge, cpf: formatCpf(e.target.value) } }))}
                        placeholder="000.000.000-00" className={inputCls}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                      <input type="email" value={form.conjuge.email || ''} onChange={setConj('email')} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Data de nascimento</label>
                      <input type="date" value={form.conjuge.dataNascimento || ''} onChange={setConj('dataNascimento')} className={inputCls} />
                    </div>
                  </div>
                  <EnderecoSection
                    value={form.conjuge.endereco || { ...EMPTY_ENDERECO }}
                    onChange={end => setForm(f => ({ ...f, conjuge: { ...f.conjuge, endereco: end } }))}
                    label="Endereço do cônjuge"
                  />
                </>
              )}
            </div>
          )}

          {erro && <p className="text-red-500 text-sm mt-4">{erro}</p>}

          <div className="flex justify-end gap-3 pt-5 mt-4 border-t border-gray-100">
            <button type="button" onClick={closeModal}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={saveMutation.isPending}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary disabled:opacity-50">
              {saveMutation.isPending ? 'Salvando...' : editing ? 'Salvar alterações' : 'Criar cliente'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
